import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Card,
  Space,
  Input,
  Modal,
  Form,
  InputNumber,
  Tag,
  Typography,
  message,
  Popconfirm,
  Select,
  Tooltip,
  Alert
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;

const Inventory = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Calculate critical items
  const getCriticalItems = () => {
    return data.filter(item => {
      if (item.usage_amount > 0 && item.usage_period !== 'none') {
        let days = Math.floor(item.quantity / item.usage_amount);
        if (item.usage_period === 'weekly') days *= 7;
        if (item.usage_period === 'monthly') days *= 30;
        return days <= 3;
      }
      return false;
    });
  };

  const criticalItems = getCriticalItems();

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventory');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      message.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/inventory/${id}`);
      message.success('Item deleted successfully');
      fetchInventory();
    } catch (error) {
      message.error('Failed to delete item');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, values);
        message.success('Item updated successfully');
      } else {
        await api.post('/inventory', values);
        message.success('Item added successfully');
      }
      setIsModalVisible(false);
      fetchInventory();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
      filteredValue: [searchText],
      onFilter: (value, record) =>
        record.product_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (quantity, record) => {
        const isLow = quantity <= record.threshold;
        
        // Stock out prediction
        let stockOutDays = null;
        if (record.usage_amount > 0 && record.usage_period !== 'none') {
          const rawDays = Math.floor(quantity / record.usage_amount);
          if (record.usage_period === 'daily') stockOutDays = rawDays;
          else if (record.usage_period === 'weekly') stockOutDays = rawDays * 7;
          else if (record.usage_period === 'monthly') stockOutDays = rawDays * 30;
        }

        const isCritical = stockOutDays !== null && stockOutDays <= 3;

        return (
          <Space direction="vertical" size={0}>
            <Space>
              <Text strong={isLow || isCritical} type={isCritical ? 'danger' : isLow ? 'warning' : ''}>
                {quantity}
              </Text>
              {isLow && !isCritical && (
                <Tag color="warning">Low Stock</Tag>
              )}
              {isCritical && (
                <Tooltip title={`Stock out expected in ${stockOutDays} day${stockOutDays === 1 ? '' : 's'}`}>
                  <Tag color="error" icon={<ExclamationCircleOutlined />}>
                    Stock Out Soon
                  </Tag>
                </Tooltip>
              )}
            </Space>
            {stockOutDays !== null && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Est. {stockOutDays} days left
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      key: 'threshold',
    },
    {
      title: 'Auto Usage',
      key: 'usage',
      render: (_, record) => {
        if (!record.usage_period || record.usage_period === 'none') return <Text type="secondary">Disabled</Text>;
        return (
          <Space direction="vertical" size={0}>
            <Text>{record.usage_amount} {record.unit} / {record.usage_period}</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Next: {record.last_usage_deduction ? new Date(new Date(record.last_usage_deduction).getTime() + 
                (record.usage_period === 'daily' ? 86400000 : 
                 record.usage_period === 'weekly' ? 604800000 : 
                 2592000000)).toLocaleDateString() : 'Pending'}
            </Text>
          </Space>
        );
      }
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="inventory-container">
      {criticalItems.length > 0 && (
        <Alert
          message="Critical Stock Alerts"
          description={
            <div>
              The following items are expected to stock out within 3 days:
              <ul>
                {criticalItems.map(item => (
                  <li key={item.id}>
                    <strong>{item.product_name}</strong> ({item.quantity} {item.unit} left)
                  </li>
                ))}
              </ul>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
          closable
        />
      )}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>Inventory Management</Title>
          <Space>
            <Input
              placeholder="Search products..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add Product
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingItem ? 'Edit Product' : 'Add New Product'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
          initialValues={{ quantity: 0, threshold: 0 }}
        >
          <Form.Item
            name="product_name"
            label="Product Name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input placeholder="e.g., Resistance Bands" />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="unit"
              label="Unit"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select placeholder="Select unit">
                <Select.Option value="kg">kg</Select.Option>
                <Select.Option value="gm">gm</Select.Option>
                <Select.Option value="litre">litre</Select.Option>
                <Select.Option value="pcs">pcs</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="threshold"
            label="Low Stock Threshold"
            tooltip="You will be notified when stock falls below this number"
            rules={[{ required: true, message: 'Please enter threshold' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Card size="small" title="Automated Usage Tracking" style={{ marginTop: 10, background: '#fafafa' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="usage_amount"
                label="Usage Amount"
                tooltip="Amount deducted automatically per period"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || value <= getFieldValue('quantity')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Usage amount cannot exceed total quantity'));
                    },
                  }),
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
              <Form.Item
                name="usage_period"
                label="Usage Period"
              >
                <Select placeholder="Select period">
                  <Select.Option value="none">None (Manual)</Select.Option>
                  <Select.Option value="daily">Daily</Select.Option>
                  <Select.Option value="weekly">Weekly</Select.Option>
                  <Select.Option value="monthly">Monthly</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Stock will be automatically deducted based on this pattern.
            </Text>
          </Card>
        </Form>
      </Modal>
    </div>
  );
};

export default Inventory;
